import { Component, ErrorInfo, ReactNode } from 'react';
import FallbackUI from './FallbackUI';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const CHUNK_RELOAD_KEY = 'chunk_reload_attempted';

function safeSessionGet(key: string): string | null {
  try { return sessionStorage.getItem(key); } catch { return null; }
}
function safeSessionSet(key: string, value: string): void {
  try { sessionStorage.setItem(key, value); } catch { /* ignore */ }
}
function safeSessionRemove(key: string): void {
  try { sessionStorage.removeItem(key); } catch { /* ignore */ }
}

function isChunkLoadError(error: Error): boolean {
  return (
    error.message.includes('Failed to fetch dynamically imported module') ||
    error.message.includes('Loading chunk') ||
    error.message.includes('Importing a module script failed')
  );
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    if (isChunkLoadError(error)) {
      const alreadyRetried = safeSessionGet(CHUNK_RELOAD_KEY) === '1';
      if (!alreadyRetried) {
        safeSessionSet(CHUNK_RELOAD_KEY, '1');
        window.location.reload();
        return;
      }
      // Second failure — fall through to FallbackUI below
    } else {
      // Clear the flag for non-chunk errors so a fresh chunk error later still gets one retry
      safeSessionRemove(CHUNK_RELOAD_KEY);
    }

    import('../../lib/sentry').then(({ captureException }) => {
      captureException(error, {
        react: {
          componentStack: errorInfo.componentStack,
        },
      });
    });

    import('../../lib/appInsights').then(({ trackAppException, flushAppInsights, SeverityLevel }) => {
      trackAppException(error, {
        componentStack: errorInfo.componentStack ?? '',
        source: 'ErrorBoundary',
      }, SeverityLevel.Critical);
      flushAppInsights();
    });
  }

  resetError = (): void => {
    safeSessionRemove(CHUNK_RELOAD_KEY);
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <FallbackUI
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          resetError={this.resetError}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
